import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { loadContacts } from '../../../features/contacts/api';
import { selectContactsByFilter } from '../../../features/contacts/contactsSlice';

/**
 * Hook for loading contacts with filters
 *
 * @param {Object} params
 * @param {number} params.customerId
 * @param {number} params.siteGroupId
 * @param {boolean} params.completeAddressOnly
 * @param {boolean} params.notDeleted
 * @param {boolean} params.primaryOnly
 * @param {number} params.limit - Maximum number of contacts to fetch (default: 10)
 * @param {string} params.search - Search query string
 * @param {string} params.ordering - Order by field (default: '-is_primary_rma_billing,-is_primary_rma_shipping,-modified')
 * @param {string[]} params.requiredFields - Array of required fields to filter by (e.g., ['email', 'name', 'address'])
 */
export const useContactFetch = ({
  customerId,
  siteGroupId,
  completeAddressOnly = true,
  notDeleted = true,
  primaryOnly = false,
  limit = 10,
  search = '',
  ordering = '-is_primary_rma_billing,-is_primary_rma_shipping,-modified',
  requiredFields = [],
  includeCustomerContacts = true, // When true and siteGroupId is set, also fetch customer-level contacts
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Convert requiredFields array to backend filter params
  const hasEmail = requiredFields.includes('email');
  const hasName = requiredFields.includes('name');
  const hasAddress = requiredFields.includes('address');
  const hasEmailOrPhone = requiredFields.includes('emailOrPhone');

  // Include customer contacts when viewing site group contacts
  const shouldIncludeCustomerContacts = siteGroupId && includeCustomerContacts;

  const filterKey = JSON.stringify({
    customer: customerId,
    site_group: siteGroupId,
    complete_address: completeAddressOnly || hasAddress,
    not_deleted: notDeleted,
    primary_only: primaryOnly,
    limit,
    search,
    ordering,
    has_email: hasEmail || undefined,
    has_name: hasName || undefined,
    has_email_or_phone: hasEmailOrPhone || undefined,
    include_customer_contacts: shouldIncludeCustomerContacts || undefined,
  });

  const contacts = useSelector(state => selectContactsByFilter(state, filterKey)) || [];

  const loadData = useCallback(
    async searchOverride => {
      setLoading(true);
      setError(null);

      try {
        await dispatch(
          loadContacts({
            customer: customerId,
            site_group: siteGroupId,
            complete_address: completeAddressOnly || hasAddress,
            not_deleted: notDeleted,
            primary_only: primaryOnly,
            limit,
            search: searchOverride !== undefined ? searchOverride : search,
            ordering,
            ...(hasEmail && { has_email: true }),
            ...(hasName && { has_name: true }),
            ...(hasEmailOrPhone && { has_email_or_phone: true }),
            ...(shouldIncludeCustomerContacts && { include_customer_contacts: true }),
          }),
        ).unwrap();
      } catch (err) {
        console.error('useContactFetch: Failed to load contacts:', err);
        setError(err.message || 'Failed to load contacts');
      } finally {
        setLoading(false);
      }
    },
    [
      dispatch,
      customerId,
      siteGroupId,
      completeAddressOnly,
      notDeleted,
      primaryOnly,
      limit,
      search,
      ordering,
      hasEmail,
      hasName,
      hasAddress,
      shouldIncludeCustomerContacts,
    ],
  );

  useEffect(() => {
    if (customerId || siteGroupId) {
      loadData();
    }
  }, [
    customerId,
    siteGroupId,
    completeAddressOnly,
    notDeleted,
    primaryOnly,
    limit,
    search,
    ordering,
    hasEmail,
    hasName,
    hasAddress,
    shouldIncludeCustomerContacts,
  ]);

  return {
    contacts,
    loading,
    error,
    refetch: loadData,
  };
};
